import { world, worldConfig } from '../../../game/demo';
import { getPOIFrom } from '../../utils';
import eventBus from '../../utils/emitter-event-bus';
import { MapConfig, MapID, NavigatorEventBus, POI, POI_ID, TravelTime } from '../navigator/navigator.model';
import { WorldEventBus } from '../world/world.model';

/** Service responsible for the map and the moving around */
export class Navigator {
  private static instance: Navigator;
  private _currentLocation!: POI;
  private currentMapConfig!: MapConfig;

  private worldPOIs = world;

  private mapElement!: HTMLElement;
  private worldElement!: HTMLElement;
  private imageElement!: HTMLImageElement;

  constructor() {
    eventBus.on(WorldEventBus.initialise, (poi) => this.initialiseMap(poi));
    eventBus.on(NavigatorEventBus.navigationStart, (poi) => this.navigationStart(poi));
  }

  public static getInstance(): Navigator {
    if (!Navigator.instance) {
      Navigator.instance = new Navigator();
    }

    return Navigator.instance;
  }

  private initialiseMap(poi: POI_ID) {
    const poiData = getPOIFrom(poi);
    const mapId = poiData?.mapId ?? worldConfig.startingMap;
    this.currentMapConfig = worldConfig.maps.find((m) => m.id === mapId) ?? worldConfig.maps[0];

    this.mapElement = document.getElementById('map')!;
    this.applyBackgroundColor();

    // Create world container
    this.worldElement = document.createElement('div');
    this.worldElement.className = 'map-world';
    this.worldElement.style.width = `${this.currentMapConfig.size[0]}px`;
    this.worldElement.style.height = `${this.currentMapConfig.size[1]}px`;

    // Create map image
    this.imageElement = document.createElement('img');
    this.imageElement.className = 'map-world__image';
    this.imageElement.src = this.currentMapConfig.imagePath;
    this.imageElement.style.width = `${this.currentMapConfig.size[0]}px`;
    this.imageElement.style.height = `${this.currentMapConfig.size[1]}px`;
    // The map is the only <img>; without this a long-press on mobile offers to
    // save/download it. CSS -webkit-touch-callout covers iOS Safari, but Android
    // Chrome raises a contextmenu event that only preventDefault() suppresses.
    this.imageElement.draggable = false;
    this.worldElement.appendChild(this.imageElement);

    this.mapElement.addEventListener('contextmenu', (event) => event.preventDefault());

    this.addMarkers();
    this.mapElement.appendChild(this.worldElement);

    if (poiData) {
      this.currentLocation = poi;
      this.centerMap(poi, false);
      this.highlightActiveMarker(poi);
    }

    eventBus.emit(NavigatorEventBus.mapChanged, this.currentMapConfig.id);
  }

  private addMarkers() {
    const currentMapPOIs = this.worldPOIs.filter((p) => p.mapId === this.currentMapConfig.id);

    // Lines are appended first so they sit behind the markers
    this.addConnectionLines(currentMapPOIs);

    // Remove existing markers
    this.worldElement.querySelectorAll('.map-world__marker').forEach((el) => el.remove());

    currentMapPOIs.forEach(({ id, pos }) => {
      const marker = document.createElement('div');
      marker.className = 'map-world__marker';
      marker.style.left = `${pos[0]}px`;
      marker.style.top = `${pos[1]}px`;
      marker.innerHTML = `<div id="${id}" class="map-world__marker-dot"></div>`;
      this.worldElement.appendChild(marker);
    });
  }

  /**
   * Draws a thin white line between every linked pair of POIs on the current map.
   * Lines share the markers' coordinate space (the POI `pos` is used directly as
   * SVG coordinates), so they pan with the camera and align exactly with markers.
   */
  private addConnectionLines(currentMapPOIs: POI[]): void {
    const SVG_NS = 'http://www.w3.org/2000/svg';

    // Remove existing overlay to avoid accumulation on rebuild
    this.worldElement.querySelector('.map-world__connections')?.remove();

    const onCurrentMap = new Set(currentMapPOIs.map((p) => p.id));
    const posById = new Map(currentMapPOIs.map((p) => [p.id, p.pos]));

    const [width, height] = this.currentMapConfig.size;

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'map-world__connections');
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

    const seen = new Set<string>();
    currentMapPOIs.forEach(({ id, pos, destination }) => {
      destination.forEach((targetId) => {
        // Only connect POIs on the current map
        if (!onCurrentMap.has(targetId)) return;

        // Dedupe bidirectional links: one line per unordered pair
        const key = id < targetId ? `${id}|${targetId}` : `${targetId}|${id}`;
        if (seen.has(key)) return;
        seen.add(key);

        const targetPos = posById.get(targetId);
        if (!targetPos) return;

        const line = document.createElementNS(SVG_NS, 'line');
        line.setAttribute('x1', `${pos[0]}`);
        line.setAttribute('y1', `${pos[1]}`);
        line.setAttribute('x2', `${targetPos[0]}`);
        line.setAttribute('y2', `${targetPos[1]}`);
        svg.appendChild(line);
      });
    });

    // Prepend so the overlay sits behind the markers
    this.worldElement.prepend(svg);
  }

  private navigationStart(poi_ID: POI_ID): void {
    if (!this.currentLocation) {
      console.error('Navigator not initialised with currentLocation');
      return;
    }

    if (!this.canNavigationStart(poi_ID)) {
      const newPoi = getPOIFrom(poi_ID);
      console.log('You can see', newPoi?.spotlight?.title, 'in the distance.', newPoi);
      return;
    }

    this.currentLocation = poi_ID;

    // Check for portal (map transition)
    if (this.currentLocation.portal) {
      const { targetMap, targetPOI } = this.currentLocation.portal;
      this.centerMap(poi_ID, true);
      setTimeout(() => this.loadMap(targetMap, targetPOI), TravelTime);
      return;
    }

    this.emitNavigationEnd();
    this.centerMap(poi_ID);
    this.highlightActiveMarker(poi_ID);
  }

  private loadMap(mapId: MapID, targetPOI: POI_ID): void {
    const newMap = worldConfig.maps.find((m) => m.id === mapId);
    if (!newMap) {
      console.error('Map not found:', mapId);
      return;
    }

    this.currentMapConfig = newMap;
    this.applyBackgroundColor();
    this.imageElement.src = newMap.imagePath;
    this.worldElement.style.width = `${newMap.size[0]}px`;
    this.worldElement.style.height = `${newMap.size[1]}px`;
    this.imageElement.style.width = `${newMap.size[0]}px`;
    this.imageElement.style.height = `${newMap.size[1]}px`;

    this.addMarkers();
    this.currentLocation = targetPOI;
    this.centerMap(targetPOI, false);
    this.highlightActiveMarker(targetPOI);
    eventBus.emit(NavigatorEventBus.mapChanged, mapId);
    this.emitNavigationEnd();
  }

  private applyBackgroundColor(): void {
    const nav = document.querySelector('.js-navigator') as HTMLElement | null;
    if (nav) {
      nav.style.backgroundColor = this.currentMapConfig.backgroundColor ?? '';
    }
  }

  centerMap(poi: POI_ID, animate = true): void {
    const marker = this.worldPOIs.find((m) => m.id === poi);
    if (!marker) return;

    const viewport = this.mapElement.getBoundingClientRect();
    const translateX = -(marker.pos[0] - viewport.width / 2);
    const translateY = -(marker.pos[1] - viewport.height * 0.72);

    // translate3d (not translate) keeps the pan on the GPU compositor on mobile
    // browsers that only reliably promote 3D transforms.
    const transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
    if (!animate) {
      this.worldElement.style.transition = 'none';
      this.worldElement.style.transform = transform;
      this.worldElement.offsetHeight; // force reflow
      this.worldElement.style.transition = '';
    } else {
      this.worldElement.style.transform = transform;
    }
  }

  private highlightActiveMarker(activeId: POI_ID): void {
    this.worldPOIs.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) {
        el.classList.toggle('is-active', id === activeId);
      }
    });
  }

  private canNavigationStart(poi_ID: POI_ID): boolean {
    return this.currentLocation.destination.includes(poi_ID);
  }

  private emitNavigationEnd(): void {
    setTimeout(() => {
      eventBus.emit(NavigatorEventBus.navigationEnd, this.currentLocation.id);
    }, TravelTime);
  }

  private set currentLocation(poi: POI | POI_ID) {
    if (typeof poi === 'string') {
      const newPoi = getPOIFrom(poi);
      if (newPoi) {
        this._currentLocation = newPoi;
      } else {
        console.error('POI', poi, 'not found');
        return;
      }
    } else {
      this._currentLocation = poi;
    }
  }

  private get currentLocation(): POI {
    return this._currentLocation;
  }
}
